import { useContext, useState, useRef } from "react"
import { FirebaseContext } from "../../firebase"
import { Link } from "react-router-dom"

import _ from "lodash"
import axios from "axios"
import { Calendar } from "primereact/calendar"

import { useQuery } from "@apollo/client"
import { SplitButton } from "primereact/splitbutton"
import DateFilterTemplate from "../../components/ReusableComponents/DateFilterTemplate"
// queries
import { useNavigate } from "react-router-dom"
import { DOCUMENTS_STAFF } from "./queries"

// Helpers
import cloudFunctionsEndpoint from "../../config/cloudFunctionsEndpoint"

// Context
import { UserContext } from "../../context/userContext"
import { parseDate } from "../../helpers/dateNormalization"
// Prime React
import { FilterMatchMode, FilterOperator } from "primereact/api"
import { MultiSelect } from "primereact/multiselect"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Toast } from "primereact/toast"

import LoaderFullPage from "../../components/Loaders"
import styles from "../../styles/Page-Engagements.module.css"
import { navigateWithOrigin } from "../../helpers/navigation"

export default function StaffDocumentManagement() {
  const [data, setData] = useState(null)

  const [error, setError] = useState()
  const [submitting, setSubmitting] = useState(false)

  const [filters] = useState({
    date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    document_title: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    document_type: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.IN }],
    },
    location: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    created_by: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    staffName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
  })

  const user = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const navigate = useNavigate()

  const { getStorage, ref, getDownloadURL } = firebase.storage

  const ID_TEMPLATE = "document_record_staff"
  const PID = user.current_participant_id || "not-set"

  const toast = useRef(null)

  const getDownloadURLFromStorage = async (path) => {
    const storage = getStorage()
    const fileRef = ref(storage, path)
    try {
      return await getDownloadURL(fileRef)
    } catch (error) {
      console.log(error)
      return null
    }
  }

  const handleDelete = async (idStamp) => {
    const endpoint = cloudFunctionsEndpoint()
    const currentPid = PID
    try {
      setSubmitting(true)
      const postBody = {
        idStamp,
        idTemplate: ID_TEMPLATE,
        organization_alias: user.organization_alias,
        organization_id: user.organization_id,
        enterprise_id: user.enterprise_id,
        enterprise_alias: user.enterprise_alias,
        participant_alias: user.participant_alias || "not-set",
        participant_membership_id: user.participant_membership_id || "not-set",
        participant_id: currentPid,
        staff_alias: user.staff_alias,
        staff_id: user.staff_id,
        values: {
          document__document_record__staff: {
            document_status: "deleted",
          },
        },
      }
      // Date normalization not needed here - this page appears to be deprecated
      await axios.post(`${endpoint}/blockEdit`, postBody)
    } catch (error) {
      setError(true)
    }
    setSubmitting(false)
  }

  const { loading, refetch } = useQuery(DOCUMENTS_STAFF, {
    variables: {
      idTemplate: ID_TEMPLATE,
      organization_id: user.organization_id,
      staff_id: user.staff_id,
    },
    fetchPolicy: "no-cache",
    skip: !user.staff_id,
    onCompleted: async (data) => {
      if (_.isEmpty(data.documentRecords)) {
        return setData(null)
      }
      const dataFormatted = JSON.parse(data.documentRecords.data)
      const handleData = async () =>
        await Promise.all(
          _.map(dataFormatted, async (blocks) => {
            const formBlock = _.find(blocks, { block_name: "form" })
            const documentBlock = _.find(blocks, {
              block_name: "document__document_record__staff",
            })
            const downloadUrl = await getDownloadURLFromStorage(
              documentBlock.values.document_staff_file
            )
            const locationObject = formBlock.values.form__location_ids[0] || {}
            const location = {
              label: locationObject.label || "",
              key: locationObject.key || "",
            }
            const staffName =
              documentBlock.values.document_staff__staff_id_selector.label || ""
            return {
              date: parseDate(formBlock.values.date_created),
              location: location.label,
              staffName,
              document_title: documentBlock.values.document_staff_name,
              document_type: documentBlock.values.document_staff_type,
              created_by: formBlock.values.staff_full_name || "N/A",
              updated_by: formBlock.values.staff_full_name || "N/A", // Wrong output...
              edit: (
                <SplitButton
                  label="View"
                  onClick={() => {
                    if (!downloadUrl) {
                      return toast.current.show({
                        severity: "error",
                        summary: "The reference to the File was not found.",
                        detail: "Please reupload the a File by clicking Edit.",
                      })
                    }
                    const getFilename = () => {
                      const splitPath = _.split(
                        documentBlock.values.document_participant_file,
                        "/"
                      )
                      return splitPath[splitPath.length - 1]
                    }
                    const filename = getFilename()
                    const xhr = new XMLHttpRequest()
                    xhr.responseType = "blob"
                    xhr.onload = () => {
                      const blob = xhr.response
                      const link = document.createElement("a")
                      link.href = URL.createObjectURL(blob)
                      link.download = filename
                      link.click()
                    }
                    xhr.open("GET", downloadUrl)
                    xhr.send()
                  }}
                  model={[
                    {
                      label: "Edit",
                      command: () => {
                        navigate(
                          `/form/${formBlock.idTemplate}/1/${formBlock.idStamp}`
                        )
                      },
                    },
                    {
                      label: "View Document Upload Session",
                      command: async () => {
                        await user.changeParticipant(formBlock.participant_id)
                        navigateWithOrigin(
                          navigate,
                          `/form/view/${formBlock.idTemplate}/${formBlock.idStamp}`
                        )
                      },
                    },
                    {
                      label: "Delete",
                      command: async () => {
                        await handleDelete(formBlock.idStamp)
                        refetch()
                      },
                    },
                  ]}
                />
              ),
            }
          })
        )
      const items = await handleData()
      setData(items)
    },
  })

  const dateBodyTemplate = (rowData) => {
    return formatDate(rowData.date)
  }

  const formatDate = (value) => {
    return value.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const documentType = [
    { label: "Certificates or Licenses", value: "Certificates or Licenses" },
    { label: "Training Documents", value: "Training Documents" },
    { label: "Personnel Files", value: "Personnel Files" },
    { label: "Other", value: "Other" },
  ]

  const docTypeFilterTemplate = (options) => {
    return (
      <MultiSelect
        value={options.value}
        options={documentType}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder="Any"
        className="p-column-filter"
      />
    )
  }

  if (loading || submitting) return <LoaderFullPage />

  if (error)
    return (
      <div>
        An error has occurred when Deleting. Please refresh your browser and try
        again.
      </div>
    )

  return (
    <div className="layout-full">
      <div className="main-content">
        <div>
          <div>
            <h1 className={styles.title}>Manage Staff Documents</h1>
            <Link to="/form/document_record_staff/1" className="btn">
              Add New Documents
            </Link>
            <br />
            <br />
            <Toast ref={toast} position="bottom-right" />
            <DataTable
              value={data}
              responsiveLayout="scroll"
              filters={filters}
              sortField="date"
              sortOrder={-1}
            >
              <Column
                field="date"
                sortable
                header="Date Uploaded"
                filterField="date"
                dataType="date"
                body={dateBodyTemplate}
                filterElement={(options) => (
                  <DateFilterTemplate
                    value={options.value}
                    filterCallback={options.filterCallback}
                    index={options.index}
                  />
                )}
                filter
                showFilterMenuOptions={false}
              />
              <Column
                field="location"
                sortable
                header="Location"
                filterPlaceholder="filter by org locations"
                showFilterMenuOptions={false}
                filter
              />
              <Column
                field="document_title"
                sortable
                filter
                showFilterMenuOptions={false}
                header="Document Title"
              />
              <Column
                field="document_type"
                sortable
                filter
                showFilterMenuOptions={false}
                header="Document Type"
                filterElement={docTypeFilterTemplate}
              />
              <Column
                field="created_by"
                sortable
                header="Created By"
                filterPlaceholder="filter by authoring staff"
                showFilterMenuOptions={false}
                filter
              />
              <Column
                field="staffName"
                sortable
                header="Staff Name"
                filterPlaceholder="filter by affected staff"
                showFilterMenuOptions={false}
                filter
              />
              <Column field="updated_by" sortable header="Last Updated By" />
              <Column field="edit" header="Actions" />
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  )
}
